'use client';

import { useEffect } from 'react';
import { GDPR } from '@/components/GDPR';
import { loadScript } from '@/utils/load-script';
import { useSiteContext } from '@/context/SiteContext';
import { PURPOSE_MAP } from '@/components/GDPR/constants';
import type { OpenSlotConfig } from '@themaven-net/smart-slots';
import type { TCF2_PURPOSE_KEYS } from '@/components/GDPR/constants';

type SmartSlotWidgetProps = {
	className?: string;
	variant: string;
};

type PurposesTypes = (typeof TCF2_PURPOSE_KEYS)[number];

// Component that renders the markup
const SmartSlotContent = ({
	attributes = {},
	className,
	scriptSrc,
	variant,
}: {
	attributes: Record<string, string>;
	className?: string;
	scriptSrc?: string;
	variant: string;
}) => {
	// Filter out the 'class' property and convert it to 'className'
	const { class: classAttr, ...otherAttributes } = attributes;

	// Split class names into arrays for better deduplication
	const classAttrArray = classAttr?.split(' ').filter(Boolean) || [];
	const classNameArray = className?.split(' ').filter(Boolean) || [];

	// Combine and deduplicate class names
	const allClasses = [...classAttrArray, ...classNameArray];
	const uniqueClasses = [...new Set(allClasses)];

	// Join back into a string
	const mergedClassName = uniqueClasses.join(' ');

	// Load script using useEffect
	useEffect(() => {
		if (scriptSrc) {
			loadScript(
				scriptSrc,
				{
					async: true,
					id: `smart-slot-script-${variant.toLowerCase()}`, // Unique ID for the script
				},
				true,
				'footer',
			);
		}
	}, [scriptSrc, variant]);

	// Always render the markup
	return <div {...otherAttributes} className={mergedClassName} />;
};

export const SmartSlotWidget = ({
	className,
	variant,
}: SmartSlotWidgetProps) => {
	const { config } = useSiteContext();
	const { smartSlotConfig } = config;
	const widgetConfig = smartSlotConfig.widgets?.[variant]
		?.config as OpenSlotConfig;
	const { attributes = {}, props } = widgetConfig || {};
	const { scriptSrc, vendorId } = props || {};

	return (
		<GDPR
			purposes={(PURPOSE_MAP[vendorId!] || []) as PurposesTypes[]}
			vendorId={vendorId!}
		>
			<SmartSlotContent
				attributes={attributes}
				className={className}
				scriptSrc={scriptSrc}
				variant={variant}
			/>
		</GDPR>
	);
};
